import { ReactElement, useState } from 'react';
import { parseZone } from 'moment/moment';
import { CartDrawerHeader } from './CartDrawerHeader';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useUpdateCartSkipMutation } from '../../services/cart/cart.queries';
import { useNotification } from '../../contexts/NotificationContext';
import { SubscriptionStatus } from '../../models/user.types';
import { useUser } from '../../contexts/UserContext';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import { SkipSurveyModal } from '../shared/modal/SkipSurveyModal';
import { getFormattedDeliveryDateRange } from '../../utils/dateTimes';
import { useAuth } from '../../contexts/AuthContext';

function CartDrawerHeaderContainer(): ReactElement {
  const { showNotification } = useNotification();
  const {
    cart,
    menu,
    setIsCartDrawerOpen,
    reactivateSubscription,
  } = useMenuWithCart();
  const {
    user,
    isActive,
  } = useUser();
  const { isLoggedIn } = useAuth();
  const updateCartMutation = useUpdateCartSkipMutation();

  const deliveryDate = menu?.deliveryOn || '';
  const formattedEstimatedDeliveryDate = getFormattedDeliveryDateRange(deliveryDate, false);
  const skipDeadline = menu?.endAt;

  const formattedDate = (dateString?: string): string => {
    if (!dateString) return '';

    return parseZone(dateString)
      .format('ddd, MMM D');
  };

  const buttonContent = {
    [SubscriptionStatus.RETURNING]: {
      buttonText: 'Resume Orders',
      heapId: 'cart_drawer_resume_orders_button',
    },
    [SubscriptionStatus.CANCELLED]: {
      buttonText: 'Reactivate Account',
      heapId: 'cart_drawer_reactivate_account_button',
    },
    [SubscriptionStatus.SUSPENDED]: {
      buttonText: 'Update Payment',
      heapId: 'cart_drawer_update_payment_button',
    },
  };

  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false);
  const handleButtonClick = async () => {
    if (user?.status === undefined) {
      return;
    }
    if (user?.status === SubscriptionStatus.SUSPENDED) {
      window.location.href = 'payment-method';
      return;
    }
    if (user?.status === SubscriptionStatus.RETURNING
      || user?.status === SubscriptionStatus.CANCELLED) {
      await reactivateSubscription();
      return;
    }
    if (cart?.isSkipped) {
      showNotification({
        content: <Toast variant={ToastVariant.CART_UNSKIPPED} />,
        duration: 5000,
      });
    } else {
      setIsSkipModalOpen(true);
    }

    if (cart) {
      updateCartMutation.mutate({
        cartId: cart.id,
        isSkipped: cart.isSkipped,
      });
    }
  };

  const handleSkipSurveyClose = () => {
    setIsSkipModalOpen(false);
    setIsCartDrawerOpen(false);
  };

  const baseText = cart?.isSkipped ? 'Unskip' : 'Skip';

  const baseHeapId = cart?.isSkipped ? 'cart_drawer_unskip_button' : 'cart_drawer_skip_button';

  const buttonText = !isActive && user?.status
    ? buttonContent[user?.status].buttonText
    : baseText;

  const heapId = !isActive && user?.status
    ? buttonContent[user?.status].heapId
    : baseHeapId;
  return (
    <>
      <CartDrawerHeader
        skipDeadline={formattedDate(skipDeadline)}
        deliveryDate={isLoggedIn ? formattedDate(deliveryDate) : formattedEstimatedDeliveryDate}
        buttonText={buttonText}
        onButtonClick={handleButtonClick}
        heapId={heapId}
      />
      {menu && isSkipModalOpen && (
        <SkipSurveyModal
          isOpen={isSkipModalOpen}
          onClose={handleSkipSurveyClose}
          menuId={menu.id}
        />
      )}
    </>
  );
}

export default CartDrawerHeaderContainer;
